import { DEV_URL, rdelete, rget, rpost } from 'utils/requests';

const getData = (response) => response?.data?.data ?? response?.data?.Data ?? [];
const getMessage = (response, fallback) => response?.data?.message || response?.data?.Message || response?.data?.error || response?.response?.data?.message || response?.response?.data?.Message || response?.response?.data?.error || fallback;
const isSuccess = (response) => response?.status >= 200 && response?.status < 300 && response?.data?.success !== false && response?.data?.Success !== false;

const normalizeMasterRateClass = (item) => ({
  rateClassId: item.rateClassId || item.RateClassId,
  rateClassName: item.rateClassName || item.RateClassName || '',
  rateCode: item.rateCode || item.RateCode || '',
  rateClassification: item.rateClassification || item.RateClassification || '',
  isResidential: Boolean(item.isResidential ?? item.IsResidential),
});

const normalizeUtilityRateClass = (item) => ({
  utilityRateClassId: item.utilityRateClassId || item.UtilityRateClassId,
  rateClassId: item.rateClassId || item.RateClassId,
  commodityId: item.commodityId || item.CommodityId,
  commodity: item.commodity || item.Commodity || '',
  rateClassName: item.rateClassName || item.RateClassName || '',
  utilityId: item.utilityId || item.UtilityId,
  utilityName: item.utilityName || item.UtilityName || '',
});

const normalizeSupplierRateClass = (item) => ({
  supplierRateClassId: item.supplierRateClassId || item.SupplierRateClassId,
  rateClassId: item.rateClassId || item.RateClassId,
  commodityId: item.commodityId || item.CommodityId,
  commodity: item.commodity || item.Commodity || '',
  rateClassName: item.rateClassName || item.RateClassName || '',
  supplierId: item.supplierId || item.SupplierId,
  supplierName: item.supplierName || item.SupplierName || '',
  utilityName: item.utilityName || item.UtilityName || '',
  masterRateClassName: item.masterRateClassName || item.MasterRateClassName || '',
});

const normalizeCommodity = (item) => ({
  value: item.commodityId || item.CommodityId || item.id || item.Id,
  label: item.commodity || item.Commodity || item.name || item.Name || '',
  commodityId: item.commodityId || item.CommodityId || item.id || item.Id,
  commodity: item.commodity || item.Commodity || item.name || item.Name || '',
});

const normalizeUtility = (item) => ({
  value: item.utilityId || item.UtilityId || item.id || item.Id,
  label: item.utilityName || item.UtilityName || item.name || item.Name || '',
  utilityId: item.utilityId || item.UtilityId || item.id || item.Id,
  utilityName: item.utilityName || item.UtilityName || item.name || item.Name || '',
});

const normalizeSupplier = (item) => ({
  value: item.supplierId || item.SupplierId || item.id || item.Id,
  label: item.supplierName || item.SupplierName || item.name || item.Name || '',
  supplierId: item.supplierId || item.SupplierId || item.id || item.Id,
  supplierName: item.supplierName || item.SupplierName || item.name || item.Name || '',
});

const normalizeSupplierCompany = (item) => {
  const enerexCompanyId = item.enerexCompanyId || item.EnerexCompanyId;
  const legalName = item.legalName || item.LegalName || '';

  return {
    label: legalName,
    value: enerexCompanyId,
    supplierId: enerexCompanyId,
    supplierName: legalName,
    companyId: item.id || item.Id,
  };
};

const assertSuccess = (response, fallback) => {
  if (!isSuccess(response)) {
    throw new Error(getMessage(response, fallback));
  }

  return response;
};

export const getMasterRateClasses = async (search = '') => {
  const query = search.trim() ? `?search=${encodeURIComponent(search.trim())}` : '';
  const response = await rget(`${DEV_URL}/api/RateClass/GetMasterRateClasses${query}`);
  assertSuccess(response, 'Failed to load master rate classes.');
  const data = getData(response);
  return Array.isArray(data) ? data.map(normalizeMasterRateClass) : [];
};

export const updateMasterRateClass = async (payload) => {
  const response = await rpost(`${DEV_URL}/api/RateClass/UpdateMasterRateClass`, payload);
  assertSuccess(response, 'Failed to update master rate class.');
  return normalizeMasterRateClass(response?.data?.data || response?.data?.Data || payload);
};

export const createMasterRateClass = async (payload) => {
  const response = await rpost(`${DEV_URL}/api/RateClass/CreateMasterRateClass`, payload);
  assertSuccess(response, 'Failed to create master rate class.');
  return normalizeMasterRateClass(response?.data?.data || response?.data?.Data || payload);
};

export const deleteMasterRateClass = async (rateClassId) => {
  try {
    const response = await rdelete(`${DEV_URL}/api/RateClass/DeleteMasterRateClass?rateClassId=${encodeURIComponent(rateClassId)}`);
    assertSuccess(response, 'Failed to delete master rate class.');
  } catch (error) {
    throw new Error(getMessage(error, error.message || 'Failed to delete master rate class.'));
  }
};

export const getUtilityRateClasses = async (rateClassId) => {
  const response = await rget(`${DEV_URL}/api/RateClass/GetUtilityRateClasses?rateClassId=${encodeURIComponent(rateClassId)}`);
  assertSuccess(response, 'Failed to load utility rate classes.');
  const data = getData(response);
  return Array.isArray(data) ? data.map(normalizeUtilityRateClass) : [];
};

export const createUtilityRateClass = async (payload) => {
  const response = await rpost(`${DEV_URL}/api/RateClass/CreateUtilityRateClass`, payload);
  assertSuccess(response, 'Failed to create utility rate class.');
  return normalizeUtilityRateClass(response?.data?.data || response?.data?.Data || payload);
};

export const deleteUtilityRateClass = async (utilityRateClassId) => {
  const response = await rdelete(`${DEV_URL}/api/RateClass/DeleteUtilityRateClass?utilityRateClassId=${encodeURIComponent(utilityRateClassId)}`);
  assertSuccess(response, 'Failed to delete utility rate class.');
};

export const getSupplierRateClasses = async (rateClassId) => {
  const response = await rget(`${DEV_URL}/api/RateClass/GetSupplierRateClasses?rateClassId=${encodeURIComponent(rateClassId)}`);
  assertSuccess(response, 'Failed to load supplier rate classes.');
  const data = getData(response);
  return Array.isArray(data) ? data.map(normalizeSupplierRateClass) : [];
};

export const getSupplierRateClassesBySupplier = async (supplierId) => {
  const response = await rget(`${DEV_URL}/api/RateClass/GetSupplierRateClassesBySupplier?supplierId=${encodeURIComponent(supplierId)}`);
  assertSuccess(response, 'Failed to load supplier rate classes.');
  const data = getData(response);
  return Array.isArray(data) ? data.map(normalizeSupplierRateClass) : [];
};

export const createSupplierRateClass = async (payload) => {
  const response = await rpost(`${DEV_URL}/api/RateClass/CreateSupplierRateClass`, payload);
  assertSuccess(response, 'Failed to create supplier rate class.');
  return normalizeSupplierRateClass(response?.data?.data || response?.data?.Data || payload);
};

export const deleteSupplierRateClass = async (supplierRateClassId) => {
  const response = await rdelete(`${DEV_URL}/api/RateClass/DeleteSupplierRateClass?supplierRateClassId=${encodeURIComponent(supplierRateClassId)}`);
  assertSuccess(response, 'Failed to delete supplier rate class.');
};

export const getCommodities = async () => {
  const response = await rget(`${DEV_URL}/api/RateClass/GetCommodities`);
  assertSuccess(response, 'Failed to load commodities.');
  const data = getData(response);
  return Array.isArray(data) ? data.map(normalizeCommodity).filter((item) => item.value && item.label) : [];
};

export const getUtilities = async (commodityId) => {
  const response = await rget(`${DEV_URL}/api/RateClass/GetUtilities?commodityId=${encodeURIComponent(commodityId || '')}`);
  assertSuccess(response, 'Failed to load utilities.');
  const data = getData(response);
  return Array.isArray(data) ? data.map(normalizeUtility).filter((item) => item.value && item.label) : [];
};

export const getRateClassSuppliers = async (commodityId) => {
  const response = await rget(`${DEV_URL}/api/RateClass/GetSuppliers?commodityId=${encodeURIComponent(commodityId || '')}`);
  assertSuccess(response, 'Failed to load suppliers.');
  const data = getData(response);
  return Array.isArray(data) ? data.map(normalizeSupplier).filter((item) => item.value && item.label) : [];
};

export const getSuppliers = async () => {
  const response = await rpost(`${DEV_URL}/api/company/getall`, {
    PageNumber: 1,
    PageSize: 1000,
    SearchTerm: '',
    orderBy: null,
    ascendingOrder: false,
    stateCode: '',
    commodity: '0',
    licenseStatus: 'Active',
    licenseType: 'Supplier',
  });
  assertSuccess(response, 'Failed to load suppliers.');
  const data = response?.data?.data ?? response?.data?.Data ?? response?.data?.company ?? response?.data?.Company ?? response?.data ?? [];
  return Array.isArray(data) ? data.map(normalizeSupplierCompany).filter((item) => item.value && item.label) : [];
};
